import React, { CSSProperties, ReactNode, useRef } from "react";
import { CircularProgress } from "@mui/material";
import { PaginatedPost } from "../../log.typing";
import { LoadingPill, TimelineFrame, TimelineScroller } from "./TimelineStyles";
import { useHeldOlderPage } from "./useHeldOlderPage";
import { useOlderPostsTrigger } from "./useOlderPostsTrigger";
import { useReadingAnchor } from "./useReadingAnchor";
import { useScrollActivity } from "./useScrollActivity";

type PostTimelineProps = {
  /** Oldest first. */
  posts: PaginatedPost[];
  isLoading: boolean;
  hasOlderPosts: boolean;
  onLoadOlder: () => void;
  /** Renders the posts actually on screen, which can lag `posts` while an older page is held back. */
  renderPosts: (posts: PaginatedPost[]) => ReactNode;
  contentOverflow?: CSSProperties["overflow"];
  style?: CSSProperties;
};

/**
 * The scrolling list of log posts, newest at the bottom. Loads older posts as the reader scrolls up,
 * and keeps the reader on the post they were reading while those are added.
 */
export const PostTimeline = ({
  posts,
  isLoading,
  hasOlderPosts,
  onLoadOlder,
  renderPosts,
  contentOverflow = "visible",
  style,
}: PostTimelineProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const isReaderScrolling = useScrollActivity(containerRef);
  const { shownPosts, isHeld, isPrepending } = useHeldOlderPage(
    posts,
    isReaderScrolling
  );
  useReadingAnchor(containerRef, contentRef, isPrepending);
  useOlderPostsTrigger(containerRef, {
    canLoad: hasOlderPosts && !isHeld,
    isLoading,
    onLoadOlder,
    shownPosts,
  });

  return (
    <TimelineFrame style={style}>
      {(isLoading || isHeld) && (
        <LoadingPill role="status" aria-live="polite">
          <CircularProgress size={16} thickness={5} sx={{ color: "#fff" }} />
          {shownPosts.length > 0 ? "Loading older posts" : "Loading posts"}
        </LoadingPill>
      )}
      <TimelineScroller ref={containerRef}>
        <div ref={contentRef} style={{ overflow: contentOverflow }}>
          {renderPosts(shownPosts)}
        </div>
      </TimelineScroller>
    </TimelineFrame>
  );
};
